<template>
  <BaseModal
    v-model="modalVisible"
    :title="modalTitle"
    titleClass="text-pink-100"
    showCloseButton
    noPadding
    contentClass="flex max-h-[86vh] w-[min(94vw,640px)] flex-col bg-linear-to-br from-[#1e1e1e] to-[#2d1f2d]"
    headerClass="!mb-0 border-b border-pink-700/35 px-4 pb-3 pt-4"
    bodyClass="!mb-0 grow overflow-y-auto px-4 py-4"
    footerClass="border-t border-pink-700/35 px-4 pb-4 pt-3"
    @cancel="onCancel"
  >
    <div class="space-y-4">
      <label class="block text-xs font-bold uppercase tracking-wide text-pink-200">
        Title <span class="text-red-300">*</span>
        <input
          v-model="form.title"
          type="text"
          class="mt-1 w-full rounded-md border border-pink-600/70 bg-white px-3 py-2 text-sm text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-300/70"
          placeholder="Activity type title"
          @keydown.enter.prevent="submitForm"
        >
      </label>

      <ColorPaletteSwatchSelector
        v-model="form.colorId"
        :colors="normalizedPalette"
        :usedValueIds="usedColorIds"
        label="Select Color"
      />

      <IconCatalogPicker
        v-model="form.iconName"
        :icons="normalizedIconOptions"
        label="Select Icon"
        searchPlaceholder="Search active icons"
        emptyMessage="No active icons match the current search. Activate more icons in Operations -> Icon Catalog."
      />

      <p v-if="errorMessage" class="rounded-md border border-red-400/45 bg-red-700/20 px-3 py-2 text-xs text-red-100">
        {{ errorMessage }}
      </p>
    </div>

    <template #footer>
      <button type="button" class="btn btn-secondary" :disabled="saving" @click="onCancel">Cancel</button>
      <button type="button" class="btn btn-primary" :disabled="isSaveDisabled" @click="submitForm">
        {{ saving ? 'Saving changes...' : saveButtonLabel }}
      </button>
    </template>
  </BaseModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import BaseModal from '@/components/modal/BaseModal.vue';
import ColorPaletteSwatchSelector from '@/components/ui/ColorPaletteSwatchSelector.vue';
import IconCatalogPicker from '@/components/ui/IconCatalogPicker.vue';

defineOptions({ name: 'ActivityTypeEditorModal' });

const DEFAULT_ACTIVITY_ICON = 'solar:walking-linear';

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  mode: {
    type: String,
    default: 'edit'
  },
  initialData: {
    type: Object,
    default: () => ({})
  },
  colorPalette: {
    type: Array,
    default: () => []
  },
  usedColorIds: {
    type: Array,
    default: () => []
  },
  iconOptions: {
    type: Array,
    default: () => []
  },
  saving: {
    type: Boolean,
    default: false
  },
  errorMessage: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:modelValue', 'submit']);

const form = ref(createDefaultForm());

const modalVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const normalizedPalette = computed(() => {
  return (Array.isArray(props.colorPalette) ? props.colorPalette : [])
    .map((color) => ({
      id: Number(color?.id),
      hex: String(color?.hex || '').trim(),
      name: String(color?.name || `Color ${color?.id ?? ''}`).trim()
    }))
    .filter((color) => Number.isFinite(color.id) && color.hex);
});
const normalizedIconOptions = computed(() => {
  const rows = (Array.isArray(props.iconOptions) ? props.iconOptions : [])
    .map((iconEntry) => ({
      iconKey: String(iconEntry?.iconKey || '').trim(),
      label: String(iconEntry?.label || iconEntry?.iconKey || '').trim(),
      category: String(iconEntry?.category || '').trim(),
      library: String(iconEntry?.library || '').trim(),
      keywords: Array.isArray(iconEntry?.keywords) ? iconEntry.keywords : []
    }))
    .filter((iconEntry) => iconEntry.iconKey);

  const currentIconKey = String(form.value.iconName || '').trim();
  if (!currentIconKey || rows.some((iconEntry) => iconEntry.iconKey === currentIconKey)) {
    return rows;
  }

  return [
    {
      iconKey: currentIconKey,
      label: currentIconKey,
      category: '',
      library: '',
      keywords: []
    },
    ...rows
  ];
});

const modalTitle = computed(() => props.mode === 'create' ? 'Add Activity Type' : 'Edit Activity Type');
const saveButtonLabel = computed(() => props.mode === 'create' ? 'Save activity type' : 'Save changes');
const isSaveDisabled = computed(() => props.saving || !String(form.value.title || '').trim());

watch(
  () => [props.modelValue, props.initialData],
  ([isOpen]) => {
    if (!isOpen) return;
    form.value = createFormFromInitialData(props.initialData);
  },
  { immediate: true, deep: true }
);

function createDefaultForm () {
  return {
    title: '',
    colorId: 20,
    iconName: DEFAULT_ACTIVITY_ICON
  };
}

function createFormFromInitialData (initialData) {
  return {
    title: String(initialData?.title || '').trim(),
    colorId: Number.isFinite(Number(initialData?.colorId)) ? Number(initialData.colorId) : 20,
    iconName: String(initialData?.iconName || DEFAULT_ACTIVITY_ICON).trim() || DEFAULT_ACTIVITY_ICON
  };
}

function onCancel () {
  modalVisible.value = false;
}

function submitForm () {
  if (isSaveDisabled.value) return;

  emit('submit', {
    title: String(form.value.title || '').trim(),
    colorId: Number.isFinite(Number(form.value.colorId)) ? Number(form.value.colorId) : 20,
    iconName: String(form.value.iconName || DEFAULT_ACTIVITY_ICON).trim() || DEFAULT_ACTIVITY_ICON
  });
}
</script>
